import React from 'react';
import type { UserAssistantConfigCreate, PhonePoolNumber } from '../../../services/adminService';
import AdminUserDetailSection from './AdminUserDetailSection';

interface AdminUserDetailAssistantSectionProps {
  editing: boolean;
  editForm: UserAssistantConfigCreate;
  setEditForm: React.Dispatch<React.SetStateAction<UserAssistantConfigCreate>>;
  assistantConfig: any;
  expanded: boolean;
  onToggle: () => void;
  onEdit: (e: React.MouseEvent) => void;
  onSave: () => void;
  onCancel: () => void;
  poolNumber: PhonePoolNumber | null;
  loadingPoolNumber: boolean;
}

const AdminUserDetailAssistantSection: React.FC<AdminUserDetailAssistantSectionProps> = ({
  editing,
  editForm,
  setEditForm,
  assistantConfig,
  expanded,
  onToggle,
  onEdit,
  onSave,
  onCancel,
  poolNumber,
  loadingPoolNumber,
}) => {
  const headerButton =
    !editing && expanded ? (
      <button
        onClick={onEdit}
        className="px-3 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg text-xs font-medium hover:from-cyan-700 hover:to-blue-700 transition-all"
      >
        Edit
      </button>
    ) : null;

  return (
    <AdminUserDetailSection
      sectionId="assistant"
      title="Assistant Configuration"
      expanded={expanded}
      onToggle={onToggle}
      headerButton={headerButton}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Outbound Assistant ID</label>
          {editing ? (
            <input
              type="text"
              value={editForm.outbound_assistant_id}
              onChange={(e) => setEditForm({ ...editForm, outbound_assistant_id: e.target.value })}
              className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Outbound Assistant ID"
            />
          ) : (
            <div className="text-xs text-gray-300 bg-white/5 px-3 py-1.5 rounded-lg break-all min-h-[2rem] flex items-center">
              {assistantConfig?.outbound_assistant_id || 'Not Set'}
            </div>
          )}
        </div>
        {/* Pool-assigned inbound phone number (read-only) */}
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Inbound Phone Number (Pool)</label>
          <div className="text-xs bg-white/5 px-3 py-1.5 rounded-lg min-h-[2rem] flex items-center gap-2">
            {loadingPoolNumber ? (
              <span className="text-gray-400">Loading...</span>
            ) : poolNumber ? (
              <>
                <span className="text-white font-mono">{poolNumber.phone_number}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  {poolNumber.number_type}/{poolNumber.country_code}
                </span>
                {poolNumber.server_url_configured ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    serverUrl OK
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                    serverUrl not set
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-400">No pool number assigned</span>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Agent Name</label>
          {editing ? (
            <input
              type="text"
              value={editForm.agent_name || ''}
              onChange={(e) => setEditForm({ ...editForm, agent_name: e.target.value })}
              autoComplete="off"
              className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="e.g., Alice, Darren"
            />
          ) : (
            <div className="text-xs text-gray-300 bg-white/5 px-3 py-1.5 rounded-lg break-all min-h-[2rem] flex items-center">
              {assistantConfig?.agent_name || 'Not Set'}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Male Voice ID</label>
          {editing ? (
            <input
              type="text"
              value={editForm.voice_id_male || ''}
              onChange={(e) => setEditForm({ ...editForm, voice_id_male: e.target.value })}
              className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Voice ID for male"
            />
          ) : (
            <div className="text-xs text-gray-300 bg-white/5 px-3 py-1.5 rounded-lg break-all min-h-[2rem] flex items-center">
              {assistantConfig?.voice_id_male || 'Not Set'}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Female Voice ID</label>
          {editing ? (
            <input
              type="text"
              value={editForm.voice_id_female || ''}
              onChange={(e) => setEditForm({ ...editForm, voice_id_female: e.target.value })}
              className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Voice ID for female"
            />
          ) : (
            <div className="text-xs text-gray-300 bg-white/5 px-3 py-1.5 rounded-lg break-all min-h-[2rem] flex items-center">
              {assistantConfig?.voice_id_female || 'Not Set'}
            </div>
          )}
        </div>
      </div>
      {editing && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700/40">
          <button
            onClick={onSave}
            className="px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-xs font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all"
          >
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-1.5 bg-white/5 text-gray-300 rounded-lg text-xs font-medium hover:bg-white/10 border border-white/20 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </AdminUserDetailSection>
  );
};

export default AdminUserDetailAssistantSection;
